
import { defineComponent, reactive, toRefs, onMounted, computed, onUnmounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import dragonHunterDetailApi from '@/api/dragonHunterDetail'
import ws from '@/websocket/index'
import globalsets from '@/utils/globalsets'
import { MESSAGEID_DEF } from '@/websocket/quotationWebsocket'
import { maskStockCode, maskStockName, isMaskEnabled } from '@/views/smallAmount/components/tableColumns'

export default defineComponent({
    name: 'DragonHunterDetail',
    setup() {
        const route = useRoute()

        const state = reactive({
            theme: 'light',
            iscode: (route.query.iscode as string) || '2',   // 1:龙头信号  2:龙头猎手
            symbol: (route.query.code as string) || '300750',
            market: (route.query.market as string) || 'sz',
            token: (route.query.token as string) || '',
            quoteData: {} as any,
            marginData: {} as any,
            snapshotData: {} as any,
            stockInfo: {
                code: (route.query.code as string) || '300750',
                name: '--',
                lastPrice: 0 as number | null,
                riseAndFall: 0 as number | null,
                increase: 0 as number | null,
                preClosePrice: 0,
                isSuspended: false
            }
        })

        const priceClass = computed(() => {
            if (state.stockInfo.riseAndFall && state.stockInfo.riseAndFall > 0) return 'red'
            if (state.stockInfo.riseAndFall && state.stockInfo.riseAndFall < 0) return 'green'
            return ''
        })

        const mcnMode = route.query.mcnMode as string | undefined

        const displayCode = computed(() =>
            isMaskEnabled(mcnMode) ? maskStockCode(state.stockInfo.code) : state.stockInfo.code
        )
        const displayName = computed(() =>
            isMaskEnabled(mcnMode) ? maskStockName(state.stockInfo.name) : state.stockInfo.name
        )
        const displayLastPrice = computed(() => {
            if (state.stockInfo.lastPrice === null || state.stockInfo.lastPrice === undefined) return '—'
            return globalsets.getNumber2(state.stockInfo.lastPrice)
        })
        const formattedChange = computed(() => {
            if (state.stockInfo.riseAndFall === null || state.stockInfo.riseAndFall === undefined) return '—'
            return globalsets.getNumber2(state.stockInfo.riseAndFall)
        })
        const formattedIncrease = computed(() => {
            const v = state.stockInfo.increase
            if (v === null || v === undefined) return '—'
            return (v > 0 ? '+' : '') + globalsets.getPencent(v)
        })

        // 龙头猎手入选类型
        const hunterSelectTypeLabel = (type: number) => {
            const map: Record<number, string> = { 5: '龙头启动', 4: '龙头加强', 3: '后排超车', 2: '二波右侧', 1: '二波左侧' }
            return map[type] || '--'
        }

        // 龙头信号入选类型
        const signalSelectTypeLabel = (type: number) => {
            const map: Record<number, string> = { 1: '龙头冲刺', 2: '龙头点火' }
            return map[type] || '--'
        }

        const formatDate = (ts: number) => {
            if (!ts) return '--'
            const d = new Date(ts)
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        }

        const formatPercent = (v: number) => {
            if (v == null) return '--'
            const pct = v * 100
            return (pct > 0 ? '' : '') + pct.toFixed(2) + '%'
        }
        const formatPercents = (v: number) => {
            if (v == null) return '--'
            const pct = v * 100
            return (pct > 0 ? '' : '') + pct.toFixed(2) + '%'
        }

        // 两融余额/热度：四舍五入保留指定小数位
        const formatWan = (v: number, precision = 2) => {
            if (v == null) return '--'
            const abs = Math.abs(v)
            const sign = v < 0 ? '-' : ''
            if (abs >= 1e8) {
                return sign + (abs / 1e8).toFixed(precision) + '亿'
            }
            return sign + (abs / 10000).toFixed(precision) + '万'
        }

        // 融资融券金额：四舍五入保留2位小数
        const formatYi = (v: number) => {
            if (v == null) return '--'
            const abs = Math.abs(v)
            const sign = v < 0 ? '-' : ''
            if (abs >= 1e8) return sign + (abs / 1e8).toFixed(2) + '亿'
            if (abs >= 1e4) return sign + (abs / 1e4).toFixed(2) + '万'
            return sign + abs.toFixed(2)
        }

        // 板块热度/个股热度：保留1位小数，不四舍五入
        const formatHot = (v: number) => {
            if (v == null) return '--'
            return formatWan(v, 1)
        }

        const formatMoney = (v: number) => {
            if (v == null) return '--'
            const abs = Math.abs(v)
            const sign = v < 0 ? '-' : ''
            if (abs >= 1e8) return sign + (abs / 1e8).toFixed(2) + '亿'
            if (abs >= 1e4) return sign + (abs / 1e4).toFixed(2) + '万'
            return sign + abs.toFixed(0)
        }

        const formatHsl = (v: number) => {
            if (v == null) return '--'
            return (v / 100).toFixed(2) + '%'
        }

        // websocket 涨幅字段：已是小数（如 0.0585），×100 显示百分比
        const formatWsPercent = (v: number) => {
            if (v == null) return '--'
            const pct = v * 100
            return (pct > 0 ? '' : '') + pct.toFixed(2) + '%'
        }

        // 接口直接返回百分比值（如 39.00），正数加 + 号
        const formatPct = (v: number) => {
            if (v == null) return '--'
            const pct = v * 100
            return (pct > 0 ? '' : '') + Number(pct).toFixed(2) + '%'
        }
        function getMonthDay(timestamp) {
            // 1. 空值判断
            if (timestamp === undefined || timestamp === null || timestamp === '') {
                return '--'; // 或返回 '00-00' 自定义异常值
            }

            // 2. 转数字
            const time = Number(timestamp);

            // 3. 非法数字判断
            if (isNaN(time) || time < 0) {
             return '--';
            }

            // 4. 生成日期
            const date = new Date(time);

            // 5. 判断是否为有效日期
            if (date.toString() === 'Invalid Date') {
                return '--';
            }

            // 6. 正常格式化
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${month}-${day}`;
        }


        const initSignalData = async () => {
            try {
                // step1: 取 tradingDay
                let tradingDay: number | null = null
                const idxRes: any = await dragonHunterDetailApi.getMultipleIndex({
                    symbol: state.symbol,
                    market: state.market,
                    dataKey: 'dragon_signal',
                    period: '6',
                    token: state.token
                })
                if (idxRes && idxRes.code === 1 && Array.isArray(idxRes.data) && idxRes.data.length > 0) {
                    // 取最新一条 signal != 0 的
                    const hit = idxRes.data.find((item: any) => item.signal !== 0)
                    if (hit) {
                        tradingDay = hit.feedTimestamp
                    }
                }
                // step2: 请求详情
                const params: any = {
                    symbol: state.symbol,
                    market: state.market,
                    token: state.token
                }
                if (tradingDay != null) {
                    params.tradingDay = tradingDay
                }
                const res: any = await dragonHunterDetailApi.getSignalDetail(params)
                if (res && res.code === 1 && res.data) {
                    const data = res.data
                    state.quoteData = data.quote || {}
                    state.marginData = data.stockMargin || {}
                    state.snapshotData = data.snapshot || data || {}
                }
            } catch (e) {
                console.error('获取龙头信号数据失败', e)
            } finally {
                subscribeQuote()
            }
        }

        // 龙头猎手：直接请求详情
        const initHunterData = async () => {
            try {
                const res: any = await dragonHunterDetailApi.getDetail({
                    symbol: state.symbol,
                    market: state.market,
                    token: state.token
                })
                if (res && res.code === 1 && res.data) {
                    const data = res.data
                    state.quoteData = data.quote || {}
                    state.marginData = data.stockMargin || {}
                    state.snapshotData = data.snapshot || data || {}
                }
            } catch (e) {
                console.error('获取龙头猎手数据失败', e)
            } finally {
                subscribeQuote()
            }
        }

        const dataTool = ref(null) as any

        const subscribeQuote = () => {
            ws.websocket.safeSubscribeDYNA(state.symbol, state.market, dataTool.value)
            ws.websocket.on('onMessage', handleMessage)
        }

        const handleMessage = (msg: any) => {
            if (msg.Inst !== state.symbol) return;

            const { QuoteData: _data } = msg;
            if (!_data) return; // 提前退出，减少嵌套

            // 解构 + 可选链简化取值
            const {
                StatisticsData = [],
                StaticData = [],
                DynaData = [],
                InstStatusData = []
            } = _data;

            // 昨日收盘价
            const [statisticItem] = StatisticsData;
            if (statisticItem) {
                state.stockInfo.preClosePrice = statisticItem.PreClosePrice;
            }

            // 股票名称
            const [staticItem] = StaticData;
            if (staticItem) {
                state.stockInfo.name = staticItem.InstrumentName;
            }

            // 动态数据
            const [dynamicItem] = DynaData;
            if (dynamicItem) {
                state.stockInfo.lastPrice = dynamicItem.LastPrice ?? 0;

                // 批量映射字段，不用写一堆 if
                const fieldMap = {
                    inmoneyflowamt: 'InMoneyFlowAmt',       // 当日主力净流入金额
                    inmoneyflowamt3day: 'InMoneyFlowAmt3Day', // 3日主力净流入金额
                    inmoneyflowamt5day: 'InMoneyFlowAmt5Day', // 5日主力净流入金额
                    inmoneyflowamt10day: 'InMoneyFlowAmt10Day', // 10日主力净流入金额
                    avgamt5day: 'AvgAmt5Day',               // 5日平均成交额
                    turnover5day: 'Turnover5Day',           // 5日平均换手率
                    amount: 'Amount',                       // 当日成交额
                    hsl: 'Hsl',                             // 换手率
                    yearUpDown: 'YearUpDown',               // 年内涨跌幅
                    upDownLastMonth: 'UpDownLastMonth',     // 近一月涨跌幅
                    newHigh: 'NewHigh'                      // 是否创新高
                };

                const snap = {};
                Object.entries(fieldMap).forEach(([targetKey, sourceKey]) => {
                    const value = dynamicItem[sourceKey];
                    if (value != null) { // 自动过滤 undefined / null
                        snap[targetKey] = value;
                    }
                });

                if (Object.keys(snap).length) {
                    state.snapshotData = { ...state.snapshotData, ...snap };
                }
            }

            // 涨跌计算
            const instStatus = InstStatusData[0]?.StatusType;
            const { lastPrice, preClosePrice } = state.stockInfo;

            if (instStatus === 6) {
                state.stockInfo.isSuspended = true;
                state.stockInfo.riseAndFall = null;
                state.stockInfo.increase = null;
                state.stockInfo.lastPrice = null;
            } else {
                state.stockInfo.isSuspended = false;
                if (lastPrice && preClosePrice) {
                    state.stockInfo.riseAndFall = lastPrice - preClosePrice;
                    state.stockInfo.increase = ((lastPrice - preClosePrice) / preClosePrice) * 100;
                }
            }
        }

        onMounted(() => {
            if (state.iscode === '1') {
                initSignalData()
            } else {
                initHunterData()
            }
        })

        onUnmounted(() => {
            if (ws.websocket) {
                ws.websocket.safeUnsubscribeDYNA(state.symbol, state.market)
                ws.websocket.off('onMessage', handleMessage)
            }
        })

        return {
            ...toRefs(state),
            priceClass,
            displayCode,
            displayName,
            displayLastPrice,
            formattedChange,
            formattedIncrease,
            hunterSelectTypeLabel,
            signalSelectTypeLabel,
            formatDate,
            formatPercent,
            formatPercents,
            formatWan,
            formatYi,
            formatHot,
            formatMoney,
            formatHsl,
            formatWsPercent,
            formatPct,
            getMonthDay
        }
    }
})
